import { IChartData, IItem } from "../interfaces";

export const PHASE_UNIT_CODE = 133;
const PHASE_TURN_DEGREES = 360;

export const isPhaseSeries = (s: IChartData) => String(s.unitCode) === String(PHASE_UNIT_CODE);

const wrapPhaseStep = (delta: number): number =>
  delta - PHASE_TURN_DEGREES * Math.floor((delta + PHASE_TURN_DEGREES / 2) / PHASE_TURN_DEGREES);

export const unwrapPhaseDegrees = (values: readonly number[]): number[] => {
  const unwrapped: number[] = [];
  let previous: number | null = null;

  values.forEach(value => {
    if (!Number.isFinite(value)) {
      unwrapped.push(value);
      return;
    }

    if (previous === null) {
      unwrapped.push(value);
      previous = value;
      return;
    }

    const next = previous + wrapPhaseStep(value - previous);
    unwrapped.push(next);
    previous = next;
  });

  return unwrapped;
};

export const withUnwrappedPhase = (series: IChartData): IChartData => {
  if (!isPhaseSeries(series) || series.items.length < 2) {
    return series;
  }

  const ordered = series.items
    .map((item, index) => ({ index, time: new Date(item.date).getTime() }))
    .filter(item => Number.isFinite(item.time))
    .sort((left, right) => left.time - right.time);
  const unwrapped = unwrapPhaseDegrees(ordered.map(({ index }) => series.items[index].value));
  let changed = false;
  const items: IItem[] = series.items.slice();

  ordered.forEach(({ index }, position) => {
    if (items[index].value === unwrapped[position]) {
      return;
    }

    changed = true;
    items[index] = { ...items[index], value: unwrapped[position] };
  });

  return changed ? { ...series, items } : series;
};

export const findPairedSeries = (series: IChartData, all: IChartData[]): IChartData | undefined =>
  all.find(s => s.idPoint === series.idPoint && isPhaseSeries(s) !== isPhaseSeries(series));

export const splitAmplitudePhase = (data: IChartData[]) => ({
  amplitudeCharts: data.filter(s => !isPhaseSeries(s)),
  phaseCharts: data.filter(isPhaseSeries),
});
